/**
 * useIdleCursor — hides the mouse cursor after `idleMs` ms of no pointer
 * movement and restores it when the pointer moves again.
 *
 * Returns `true` when the cursor should be hidden so the caller can apply
 * `style={{ cursor: 'none' }}` to the kiosk root element.
 */
import { useEffect, useRef, useState } from 'react';

export function useIdleCursor(idleMs = 3000): boolean {
  const [idle, setIdle] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function resetTimer() {
      setIdle(false);
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setIdle(true), idleMs);
    }

    // Start the initial timer.
    resetTimer();

    window.addEventListener('pointermove', resetTimer);
    window.addEventListener('pointerdown', resetTimer);

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      window.removeEventListener('pointermove', resetTimer);
      window.removeEventListener('pointerdown', resetTimer);
    };
  }, [idleMs]);

  return idle;
}
